import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["sx", "children", "errorMessage", "includeBothMessages"];
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { FormHelperText as CoreFormHelperText, useThemeProps } from '@mui/material';
import { ensureDotAtEnd } from '@lumahealthhq/ui-utils';
import Typography from '../Typography';
import { extendSX } from '../helpers';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
/**
 *
 * Demos:
 *
 * - [TextField](https://ui-packages.isbuildingluma.com/ui-core/react-text-field/)
 *
 * API:
 *
 * - [FormHelperText API](https://ui-packages.isbuildingluma.com/ui-core/api/form-helper-text/)
 * - inherits [MuiFormHelperText API](https://mui.com/material-ui/api/form-helper-text/)
 */
function FormHelperText(inProps) {
  const props = useThemeProps({
    props: inProps,
    name: 'LumaFormHelperText'
  });
  const {
      sx,
      children: childrenProp,
      errorMessage: errorMessageProp,
      includeBothMessages
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const children = useMemo(() => {
    if (typeof childrenProp === 'string') {
      return ensureDotAtEnd(childrenProp);
    }
    return childrenProp;
  }, [childrenProp]);
  const errorMessage = useMemo(() => ensureDotAtEnd(errorMessageProp), [errorMessageProp]);
  if (!children && !errorMessage) {
    return null;
  }
  if (includeBothMessages && children && errorMessage) {
    return /*#__PURE__*/_jsxs(CoreFormHelperText, _extends({
      component: "div",
      sx: extendSX(sx, {
        m: 0
      })
    }, rest, {
      children: [/*#__PURE__*/_jsx(Typography, {
        component: "p",
        variant: "caption",
        color: "text.primary",
        gutterBottom: true,
        children: children
      }), errorMessage]
    }));
  }
  return /*#__PURE__*/_jsx(CoreFormHelperText, _extends({
    sx: extendSX(sx)
  }, rest, {
    error: rest.error || !!errorMessage,
    children: errorMessage || children
  }));
}
process.env.NODE_ENV !== "production" ? FormHelperText.propTypes /* remove-proptypes */ = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit "FormHelperTextProps.ts" and run "yarn proptypes"
  // ----------------------------------------------------------------------
  /**
   * The content of the component.
   *
   * If `' '` is provided, the component reserves one line height for displaying a future message.
   */
  children: PropTypes.node,
  /**
   * If `true`, helper text should be displayed in an error state.
   */
  error: PropTypes.bool,
  /**
   * @ignore
   */
  errorMessage: PropTypes.string,
  /**
   * @ignore
   */
  includeBothMessages: PropTypes.bool,
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])), PropTypes.func, PropTypes.object])
} : void 0;
export default FormHelperText;